'use client';

import * as React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

type AnimatedBeamProps = {
  containerRef: React.RefObject<HTMLElement | HTMLDivElement>;
  fromRef: React.RefObject<HTMLElement | HTMLDivElement>;
  toRef: React.RefObject<HTMLElement | HTMLDivElement>;
  className?: string;
  curvature?: number; // +/- px for the curve intensity
  endYOffset?: number; // px to offset end Y
  reverse?: boolean; // reverse dash direction
  color?: string;
  width?: number; // stroke width
};

function getCenter(el: Element, root: DOMRect) {
  const r = el.getBoundingClientRect();
  return {
    x: r.left - root.left + r.width / 2,
    y: r.top - root.top + r.height / 2,
  };
}

export function AnimatedBeam({
  containerRef,
  fromRef,
  toRef,
  className,
  curvature = 60,
  endYOffset = 0,
  reverse = false,
  color = 'rgba(99,102,241,1)',
  width = 3.5,
}: AnimatedBeamProps) {
  const [d, setD] = React.useState<string>('');
  const uid = React.useId().replace(/:/g, '') + '_' + Math.random().toString(36).substr(2, 9);
  const gradId = `beamGradient_${uid}`;
  const filterId = `beamShadow_${uid}`;
  const [debugPts, setDebugPts] = React.useState<{ a:{x:number;y:number}; b:{x:number;y:number}; c1:{x:number;y:number}; c2:{x:number;y:number} }|null>(null);

  const recalc = React.useCallback(() => {
    const rootEl = containerRef.current as HTMLElement | null;
    const fromEl = fromRef.current as HTMLElement | null;
    const toEl = toRef.current as HTMLElement | null;
    if (!rootEl || !fromEl || !toEl) return;
    const rootRect = rootEl.getBoundingClientRect();
    const a = getCenter(fromEl, rootRect);
    const b = getCenter(toEl, rootRect);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const n = Math.hypot(dx, dy) || 1;
    const ux = -dy / n; // perpendicular unit vector x
    const uy = dx / n; // perpendicular unit vector y
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y + endYOffset) / 2;
    const cx = mx + ux * curvature;
    const cy = my + uy * curvature;
    const c1x = (a.x + cx) / 2;
    const c1y = (a.y + cy) / 2;
    const c2x = (b.x + cx) / 2;
    const c2y = (b.y + cy) / 2;
    const path = `M ${a.x},${a.y} C ${c1x},${c1y} ${c2x},${c2y} ${b.x},${b.y}`;
    setD(path);
    setDebugPts({ a, b, c1: {x:c1x, y:c1y}, c2: {x:c2x, y:c2y} });
  }, [containerRef, fromRef, toRef, curvature, endYOffset]);

  React.useEffect(() => {
    recalc();
    // Recalculate on next frames to ensure refs are mounted
    const raf1 = requestAnimationFrame(recalc);
    const raf2 = requestAnimationFrame(recalc);
    const ro = new ResizeObserver(recalc);
    if (containerRef.current) ro.observe(containerRef.current);
    if (fromRef.current) ro.observe(fromRef.current);
    if (toRef.current) ro.observe(toRef.current);
    // Observe DOM changes inside container to catch late mounts
    const mo = new MutationObserver(recalc);
    if (containerRef.current) mo.observe(containerRef.current, { childList: true, subtree: true });
    window.addEventListener('scroll', recalc, true);
    window.addEventListener('resize', recalc);
    return () => {
      ro.disconnect();
       mo.disconnect();
       cancelAnimationFrame(raf1);
       cancelAnimationFrame(raf2);
      window.removeEventListener('scroll', recalc, true);
      window.removeEventListener('resize', recalc);
    };
  }, [recalc, containerRef, fromRef, toRef]);

  return (
    <svg className={cn('pointer-events-none absolute inset-0', className)} style={{ zIndex: 20, mixBlendMode: 'normal', background: 'rgba(255, 255, 255, 0.01)', width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity={0.4} />
          <stop offset="50%" stopColor={color} stopOpacity={1} />
          <stop offset="100%" stopColor={color} stopOpacity={0.4} />
        </linearGradient>
        <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
        </filter>
      </defs>
      <motion.path
        d={d}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={width}
        strokeLinecap="round"
        strokeDasharray="10 12"
        filter={`url(#${filterId})`}
        animate={{ strokeDashoffset: reverse ? [36, 0] : [-36, 0] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: 'linear' }}
      />
    </svg>
  );
}
